import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OnboardingTour: a click-driven product walkthrough for demo-link (invite_lead) visitors.
 *
 * It is NOT an auto-driver. The visitor clicks the REAL buttons (a real grade, a real game,
 * the real Launch); the app navigates with its own working code; this coach simply points at
 * the next thing and advances when the route actually changes. So it rides the app's real,
 * already-working navigation instead of forcing it. The coach's Next is always there as a
 * safety so no one is ever trapped, but the spotlight invites the real tap, which is what
 * makes a prospect feel the product instead of watching it.
 *
 * Reuses the SAME trigger as the old OnboardingModal (role 'invite_lead' plus the
 * sessionStorage flag set by InviteLanding, or onboarding_completed === false), so the
 * magic-link entry and per-device behavior are unchanged. Zero new dependencies. No em-dashes.
 * Demo visitors cannot skip.
 */

const DEMO = { game: 'la_course', grade: '4', month: 'september' };
// Tour uses normal game settings so the visitor sees real content depth. The
// gameplay step renders a non-blocking popup so they can tap a few questions
// (hear the sounds, watch the canoes move), then "Skip to results" jumps them
// to the end-of-round screen without playing the full round.
const QS = `mode=school&grade=${DEMO.grade}&month=${DEMO.month}`;

// Program we walk the visitor through for the second half of the tour. Stable id,
// always present in seed data, used by the "Step inside a program" beat.
//
// Branching by account_type:
//   - school accounts (public + private schools) -> 09_private_school_gr4 (the
//     classroom-shape program with grade-4 content).
//   - private_center accounts (after-school centres, coaching institutes,
//     summer camps) -> 02_afterschool_beginner. Schools' private-school program
//     is not appropriate for non-school audiences and walking them through it
//     gives the wrong impression of how they'd use FrenchBoard.
const DEMO_PROGRAM_ID_SCHOOL = '09_private_school_gr4';
const DEMO_PROGRAM_ID_PRIVATE_CENTER = '02_afterschool_beginner';
const programIdForAccount = (accountType) =>
  accountType === 'private_center' ? DEMO_PROGRAM_ID_PRIVATE_CENTER : DEMO_PROGRAM_ID_SCHOOL;

// `nav` steps advance when the visitor's real tap changes the route. `next` steps advance
// with the coach button. Anchors are real, repo-verified data-testids.
//
// `reverseFromNext` (optional): describes how to restore THIS step's state when the visitor
// presses Back from the NEXT step. Undoes whatever this step's forward (Next) action did.
//   - click: testid to click (close a modal we opened, exit review mode, re-open a panel
//     we closed on Next, etc.)
//   - navigateTo: explicit URL to navigate to instead of just using prev.path (used when
//     game-bound state cannot be restored, so we send the visitor to the setup screen for
//     a clean restart point)
const SETUP_URL = `/play/${DEMO.game}/setup?${QS}`;

// Build the step list for the given account_type. The first half (Browse Games
// through /progress + Programs sidebar nav) is identical for both audiences;
// only the program walk-through diverges.
//   - school accounts walk through a school program with grade-4 content.
//   - private_center accounts walk through an after-school program. Download
//     PDF for the day-scope is intentionally hidden for private centres (it
//     would invite copy-and-cancel after a few months), so the tour skips that
//     beat. Home Sheet is available for private centres and the tour keeps it.
function buildSteps(accountType) {
  const isPrivateCenter = accountType === 'private_center';
  const DEMO_PROGRAM_ID = programIdForAccount(accountType);
  const programsCatalogCopy = isPrivateCenter
    ? {
        sidebarTitle: 'Prefer a term built for you?',
        sidebarBody: "Everything so far is the main way you'd use FrenchBoard: plugging games into whatever you're teaching, any session of the term. But if you'd rather have a full term built for you, that's here too. Tap Programs.",
        catalogTitle: 'Full programs, laid out as cards',
        catalogBody: "These are complete after-school terms, session by session, and each card's scope is right there: what students can do, the vocabulary and phrases, the cultural anchor, and the curriculum it maps to. Tap a program to step inside.",
        sessionScopeBody: "Each Scope opens one session at a time: the vocabulary and phrases covered, what students can do, and how it lines up with each province's expectations. We've done this for every session of the term, across all nine provinces.",
        homeSheetBody: "And a printable Home Sheet, a one-page recall students take home to reinforce exactly what they did in class. Across all nine provinces. Try this one, then tap Next.",
      }
    : {
        sidebarTitle: 'Prefer the year built for you?',
        sidebarBody: "Everything so far is the main way you'd use FrenchBoard: plugging games into whatever you're teaching, any time of the year. But if you'd rather have the whole year built for you, that's here too. Tap Programs.",
        catalogTitle: 'Full programs, laid out as cards',
        catalogBody: "These are full programs, one lesson at a time, and each card's scope is right there: what students can do, the vocabulary and phrases, the cultural anchor, and the curriculum it maps to. Tap a program to step inside.",
        sessionScopeBody: "Each Scope opens one day at a time: the vocabulary and phrases covered, what students can do, and how it lines up with each province's expectations. We've done this for every day of the ten-month year, across all nine provinces.",
        homeSheetBody: "And a printable Home Sheet, a one-page daily recall students take home to reinforce exactly what they did in class. Two downloads, every day, across all nine provinces. Try this one too, then tap Next.",
      };

  const steps = [
  { path: '/browse', anchor: 'grade-4-button', placement: 'bottom', kind: 'nav',
    openOnEnter: 'mode-school-button', rescrollMs: 3000,
    title: 'Browse Games',
    body: "This is Browse Games, and it's the heart of FrenchBoard. Ready-to-go games you plug into whatever you're already teaching, any day of the year, with no prep. Find anything three ways: school year, level, or topic. Tap a grade to begin." },
  { path: `/school/${DEMO.grade}`, anchor: `month-${DEMO.month}-button`, placement: 'bottom', kind: 'nav',
    title: 'Sorted by month, level, and topic',
    body: "Everything underneath is already sorted by month, by level, and by the vocabulary it covers. Tap the month you're teaching." },
  { path: `/games?${QS}`, anchor: `game-card-${DEMO.game}`, placement: 'bottom', kind: 'nav',
    title: 'Open a game',
    body: 'Each card is ready to project. Tap La Course, a quick two-team canoe race we will run as our demo round.' },
  { path: SETUP_URL, anchor: 'preview-toggle', placement: 'left', kind: 'next',
    title: 'You land here first, not on the board',
    body: 'A setup screen where you stay in control: difficulty, name your teams, choose a target score and a timer. Open Preview to see every question right here, before the class ever sees it, then tap Next.' },
  { path: SETUP_URL, anchor: ['curriculum-map-modal', 'curriculum-map-button'], placement: 'left', kind: 'next',
    clickOnAdvance: 'curriculum-map-button',
    reverseFromNext: { click: 'curriculum-map-close' },
    title: 'Mapped to your curriculum',
    body: "Every item is mapped to the exact expectation it teaches, in each province's own wording, across all nine provinces and every CEFR level. That's an enormous amount of alignment work, done for you. Open Curriculum Map to scan, then tap Next." },
  { path: SETUP_URL, anchor: 'curriculum-map-modal', placement: 'left', kind: 'next',
    closeOnAdvance: 'curriculum-map-close',
    reverseFromNext: { click: 'curriculum-map-button' },
    title: 'Mapped to your curriculum',
    body: "Every item is mapped to the exact expectation it teaches, in each province's own wording, across all nine provinces and every CEFR level. That's an enormous amount of alignment work, done for you. Open Curriculum Map to scan, then tap Next." },
  { path: SETUP_URL, anchor: 'start-game-button', placement: 'top', kind: 'nav',
    title: 'Start the round',
    body: "When you start it, it takes over the whole board. Students play in teams, as many as you like, competing by answering in French, so everyone's in it together. Tap Start Game (Smartboard takes over the board in your real class; we use Start here so the tour can stay with you)." },
  { path: `/play/${DEMO.game}`, anchor: null, placement: 'bottom-right', kind: 'next',
    noOverlay: true, nextLabel: 'Skip to results', endGameOnAdvance: true,
    requiresAnswered: 2,
    reverseFromNext: { navigateTo: SETUP_URL },
    title: 'Try a few rounds',
    body: "In real class, students line up by team and take turns at the board. Tap a few options to feel the flow, then you may Skip to results." },
  { path: `/play/${DEMO.game}`, anchor: null, placement: 'center', kind: 'next',
    noOverlay: true, popupDelay: 3000,
    title: "Don't miss the recap",
    body: "When the round wraps, you land here. The game makes it fun. The recap is what makes it stick. Tap Next to head to Review with Answers." },
  { path: `/play/${DEMO.game}`, anchor: 'review-answers-button', placement: 'left-pin', kind: 'next',
    noOverlay: true, ringTight: true, ringBottomTrim: 6, rescrollMs: 5000,
    clickOnAdvance: 'review-answers-button',
    reverseFromNext: { click: 'review-exit-btn' },
    title: 'Review with Answers',
    body: "Tap Review with Answers (or Next, here on the tour) and the whole class comes back together. You step through every question one at a time, both teams' answers side by side, with the correct one highlighted." },
  { path: `/play/${DEMO.game}`, anchor: null, placement: 'bottom-left', kind: 'next',
    noOverlay: true, popupDelay: 800, floatFallback: 'bottom-left',
    navigateOnAdvance: '/progress',
    reverseFromNext: { navigateTo: SETUP_URL },
    title: 'A second, calmer pass',
    body: "The whole class comes back together for a calmer pass through every question. Both teams' answers side by side, with the correct one in green. The game makes it fun. This recap is what makes it stick. Tap Next to continue." },
  { path: '/progress', anchor: null, placement: 'center', kind: 'next',
    title: 'Progress, on its own',
    body: 'Everything the class plays feeds in here automatically. See exactly what each class has covered, by level or by provincial curriculum. Tap Next.' },
  { path: '/progress', anchor: ['download-report-modal', 'open-download-report'], placement: 'left', kind: 'next',
    clickOnAdvance: 'open-download-report',
    reverseFromNext: { click: 'close-report-modal' },
    title: 'One clean PDF, when you need it',
    body: "When you need a record, open Download Report. Choose the curricula you want, pick your time period, and you get one clean PDF of everything covered. No spreadsheets, no manual tracking. Tap Next." },
  { path: '/progress', anchor: 'download-report-modal', placement: 'left', kind: 'next',
    closeOnAdvance: 'close-report-modal',
    reverseFromNext: { click: 'open-download-report' },
    title: 'One clean PDF, when you need it',
    body: "When you need a record, open Download Report. Choose the curricula you want, pick your time period, and you get one clean PDF of everything covered. No spreadsheets, no manual tracking. Tap Next." },
  { path: '/progress', anchor: 'sidebar-nav-programs', placement: 'right', kind: 'nav',
    title: programsCatalogCopy.sidebarTitle,
    body: programsCatalogCopy.sidebarBody },
  { path: '/programs', anchor: `program-card-${DEMO_PROGRAM_ID}`, placement: 'bottom', kind: 'nav',
    rescrollMs: 4000,
    title: programsCatalogCopy.catalogTitle,
    body: programsCatalogCopy.catalogBody },
  { path: `/programs/${DEMO_PROGRAM_ID}`, anchor: 'session-block-0-0-0', placement: 'bottom', kind: 'next',
    rescrollMs: 4000,
    blockAnchorClicks: true,
    title: 'Inside a lesson',
    body: "Step inside a lesson, and you'll find the teaching slides with the games built right in, each one mapped to the curriculum, exactly like the Browse Games. Tap Next." },
  { path: `/programs/${DEMO_PROGRAM_ID}`, anchor: 'session-block-0-0-0-scope-btn', placement: 'left', kind: 'next',
    rescrollMs: 4000,
    blockAnchorClicks: true,
    clickOnAdvance: 'session-block-0-0-0-scope-btn',
    reverseFromNext: { click: 'day-scope-close' },
    title: isPrivateCenter ? 'Every session has a Scope' : 'Every day has a Scope',
    body: isPrivateCenter
      ? "On the right of each session row is a Scope button (highlighted here). Tap Next to open it."
      : "On the right of each session row is a Scope button (highlighted here). Tap Next to open it." },
  { path: `/programs/${DEMO_PROGRAM_ID}`, anchor: ['day-scope-panel', 'session-block-0-0-0-scope-btn'], placement: 'left', kind: 'next',
    rescrollMs: 4000,
    noOverlay: true,
    title: isPrivateCenter ? 'A scope for every session' : 'A scope for every day',
    body: programsCatalogCopy.sessionScopeBody },
  ];

  // School-only beat: download the day's lesson card PDF. Hidden on
  // private_center accounts because the Download PDF button is intentionally
  // gated off for them (Home Sheet is the only download they get).
  if (!isPrivateCenter) {
    steps.push({
      path: `/programs/${DEMO_PROGRAM_ID}`, anchor: 'day-scope-download-btn', placement: 'left', kind: 'next',
      noOverlay: true,
      title: 'Download the day, mapped to the curriculum',
      body: "One clean PDF for the day: the vocabulary, what students can do, and how it lines up with every province's expectations. Go ahead, tap Download PDF and try it. Then tap Next.",
    });
  }

  steps.push(
    { path: `/programs/${DEMO_PROGRAM_ID}`, anchor: 'day-scope-home-sheet-btn', placement: 'left', kind: 'next',
      noOverlay: true,
      closeOnAdvance: 'day-scope-close',
      reverseFromNext: { click: 'session-block-0-0-0-scope-btn' },
      title: 'A Home Sheet to send home',
      body: programsCatalogCopy.homeSheetBody },
    { path: `/programs/${DEMO_PROGRAM_ID}`, anchor: 'sidebar-nav-guide', placement: 'right', kind: 'nav',
      title: 'A guide to all of it',
      body: "There's also a guide that gives you a quick head start on all of it. Tap Guide." },
    { path: '/guide', anchor: null, placement: 'center', kind: 'next',
      title: 'That was the quick tour',
      body: "We only opened one game. There are plenty more, each a different way to play. You're now on the Guide page. If you'd rather watch than read, there's a quick video walkthrough at the top of this page, about 4 minutes. Otherwise, your demo account is open: take your time, try a few games, and picture them in your own class. Merci, and enjoy FrenchBoard." },
  );

  return steps;
}

const norm = (p) => (p || '').split('?')[0];

// Resolve a step's spotlight target. Accepts either a single anchor string or
// an array of candidates (first found in the DOM wins) so a step can prefer a
// slide-out panel once opened and fall back to the button that opens it.
const anchorCandidates = (step) =>
  (Array.isArray(step?.anchor) ? step.anchor : (step?.anchor ? [step.anchor] : [])).filter(Boolean);

const findAnchorEl = (step) => {
  for (const a of anchorCandidates(step)) {
    const el = document.querySelector(`[data-testid="${a}"]`);
    if (el) return el;
  }
  return null;
};

export function OnboardingTour() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [showCoach, setShowCoach] = useState(true);
  const [answeredCount, setAnsweredCount] = useState(0);
  const poll = useRef(null);
  // Set briefly when the visitor taps Back so the path-watcher does not undo
  // the rewind by snapping i forward to whatever step matches the OLD URL we
  // are about to leave.
  const backIntent = useRef(false);

  // Per-account-type step list. Schools (public + private) walk through a
  // school program; private-centre accounts (after-school, camps, coaching)
  // walk through an after-school program with the download-PDF beat removed
  // (the button itself is gated off for them).
  const STEPS = useMemo(() => buildSteps(user?.account_type), [user?.account_type]);

  // Demo visitors no longer get the tour auto-fired on land (it blacked-out the
  // first impression). They start it themselves from the sidebar "Take the tour"
  // button in DemoFooter (which dispatches frenchboard:start-tour). Admins can
  // still preview from the Platform Admin banner. Auto-fire is intentionally
  // removed — first impression matters more than guided onboarding here.

  // Manual re-launch from anywhere (e.g., sidebar "Take the tour", admin preview).
  useEffect(() => {
    const onStart = () => { setI(0); setAnsweredCount(0); setActive(true); };
    window.addEventListener('frenchboard:start-tour', onStart);
    return () => window.removeEventListener('frenchboard:start-tour', onStart);
  }, []);

  // Track how many questions the visitor has answered in the demo game so we
  // can keep "Skip to results" disabled until the EndScreen has real stats.
  useEffect(() => {
    const onAnswer = (e) => {
      const n = e?.detail?.count;
      if (typeof n === 'number') setAnsweredCount(n);
    };
    window.addEventListener('frenchboard:answer-recorded', onAnswer);
    return () => window.removeEventListener('frenchboard:answer-recorded', onAnswer);
  }, []);

  // Reset the answered counter whenever the visitor enters or leaves the
  // gameplay step so a re-run of the tour starts from zero.
  useEffect(() => {
    const cur = STEPS[i];
    if (!cur || !cur.requiresAnswered) setAnsweredCount(0);
  }, [i, STEPS]);

  // While the tour is active, defensively exit browser fullscreen if a click
  // (e.g. an accidental tap on SMARTBOARD) put the page into it. Fullscreen
  // hides body-level elements (including this coach popup), which would strand
  // the visitor mid-tour.
  useEffect(() => {
    if (!active) return;
    const exitIfNeeded = () => {
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    };
    exitIfNeeded();
    document.addEventListener('fullscreenchange', exitIfNeeded);
    return () => document.removeEventListener('fullscreenchange', exitIfNeeded);
  }, [active]);

  const step = STEPS[i];

  // Some steps need a collapsed panel opened (e.g. Week 1 toggle on the program
  // schedule) so the next spotlight has a real anchor to land on. Programmatic
  // click on enter so the visitor doesn't have to find it themselves.
  useEffect(() => {
    if (!active || !step?.openOnEnter) return;
    const tryClick = () => {
      const el = document.querySelector(`[data-testid="${step.openOnEnter}"]`);
      if (el) { el.click(); return true; }
      return false;
    };
    if (tryClick()) return;
    let tries = 0;
    const id = setInterval(() => {
      tries += 1;
      if (tryClick() || tries > 20) clearInterval(id);
    }, 150);
    return () => clearInterval(id);
  }, [active, i, step]);

  // Hold the popup for a beat on steps that follow a celebratory moment
  // (e.g. the EndScreen confetti). The dim+coach don't intrude until the
  // visitor has had time to absorb what just happened.
  useEffect(() => {
    if (!active || !step) return;
    if (step.popupDelay) {
      setShowCoach(false);
      const t = setTimeout(() => setShowCoach(true), step.popupDelay);
      return () => clearTimeout(t);
    }
    setShowCoach(true);
  }, [active, i, step]);

  // Re-trigger scrollIntoView on the anchor for `rescrollMs` ms after the step
  // activates. Some target pages defer-render heavy chunks (EndScreen's question
  // review + curriculum summary appear after the initial paint, pushing the
  // "Review with Answers" button below the fold). The single one-shot scroll in
  // the spotlight setup runs too early — by the time the page settles, the
  // anchor is offscreen and a teacher who taps Next instead of scrolling skips
  // the whole Review feature. This guard keeps the anchor visible during the
  // reflow window without fighting the visitor's own scroll later.
  //
  // Implementation notes (hard-won):
  //   - Use behavior:'auto' (instant). 'smooth' restarts the animation on every
  //     poll and the scroll never converges within the poll window.
  //   - Track lastBottom so we only re-scroll when the rect has actually moved;
  //     prevents fighting GSAP's stagger animation on EndScreen children.
  //   - Fallback: if scrollIntoView doesn't move the offending ancestor (rare,
  //     happens when an `overflowY:auto` ancestor's clientHeight equals its
  //     scrollHeight at the moment of the call), explicitly walk up and set
  //     scrollTop on the nearest scrollable parent.
  // Re-trigger scrollIntoView on the anchor for `rescrollMs` ms after the step
  // activates. Some target pages defer-render heavy chunks (EndScreen's question
  // review + curriculum summary appear after the initial paint, pushing the
  // "Review with Answers" button below the fold). The single one-shot scroll in
  // the spotlight setup runs too early — by the time the page settles, the
  // anchor is offscreen and a teacher who taps Next instead of scrolling skips
  // the whole Review feature.
  //
  // Implementation notes (hard-won):
  //   - behavior:'auto' (instant) — 'smooth' restarts on every poll and never
  //     converges within the window.
  //   - Threshold uses raw window.innerHeight (no buffer) so we stop the moment
  //     the anchor is actually in view. An earlier version used a 80px buffer
  //     plus a manual `scrollTop = el.offsetTop - clientHeight/2 + ...` fallback,
  //     but `offsetTop` is relative to the *offsetParent* (not the scrollable
  //     container) and produced negative targets that the Math.max clamped to 0
  //     — which actively *undid* the successful scrollIntoView from the same
  //     poll tick. Trust scrollIntoView, no manual fallback.
  useEffect(() => {
    if (!active || !step?.rescrollMs) return;
    const start = Date.now();
    let succeeded = false;
    const id = setInterval(() => {
      if (succeeded || Date.now() - start > step.rescrollMs) { clearInterval(id); return; }
      const el = findAnchorEl(step);
      if (!el) return;
      const r = el.getBoundingClientRect();
      const inView = r.top >= 0 && r.bottom <= window.innerHeight;
      if (inView) { succeeded = true; clearInterval(id); return; }
      try { el.scrollIntoView({ block: 'center', behavior: 'auto' }); } catch { /* noop */ }
    }, 250);
    return () => clearInterval(id);
  }, [active, i, step]);

  // Enter a step: find the anchor on the current page. If the visitor has navigated FORWARD to a
  // later step's page (real tap), advance the tour to match — never yank them back, never strip
  // query params with a fresh navigate(). Only navigate forward when the coach is genuinely
  // ahead of the visitor (e.g., on first mount before they've clicked anything).
  useEffect(() => {
    if (!active || !step) return;
    const locBase = norm(location.pathname);
    const stepBase = norm(step.path);

    // If the visitor just tapped Back, skip the forward-jump search this tick
    // (otherwise the still-stale location would match a later step's path and
    // we would silently undo the rewind).
    if (backIntent.current) {
      backIntent.current = false;
      if (locBase !== stepBase) { setRect(null); return; }
    } else {
      // The visitor's real tap may have moved them to a LATER page than this step's. Advance to the
      // first later step whose path matches the new page. Skip later steps that share the current
      // step's page — those represent additional in-page beats the visitor still needs to see.
      for (let k = i + 1; k < STEPS.length; k++) {
        const candBase = norm(STEPS[k].path);
        if (candBase !== stepBase && candBase === locBase) { setI(k); return; }
      }

      if (locBase !== stepBase) {
        // Visitor is on a route that is neither this step nor any later step. Hide the spotlight
        // and wait quietly. Do NOT auto-navigate — that risks stripping query params the app needs.
        setRect(null);
        return;
      }
    }

    if (!anchorCandidates(step).length) { setRect(null); return; }
    // Clear stale rect from the previous step before hunting the new anchor so
    // the popup never paints at the old position while the new anchor mounts
    // (caused the "Prefer the year built for you?" popup to flash at the
    // download-report button before snapping to the Programs sidebar).
    setRect(null);
    let tries = 0;
    const find = () => {
      const el = findAnchorEl(step);
      if (!el) return false;
      // Set rect immediately to the anchor's current viewport position so the
      // popup paints in the right place on the first frame. The 200ms refresh
      // below catches any shift caused by the smooth scrollIntoView.
      const r0 = el.getBoundingClientRect();
      setRect({ top: r0.top, left: r0.left, width: r0.width, height: r0.height });
      try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch { /* noop */ }
      setTimeout(() => {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }, 200);
      return true;
    };
    if (find()) {
      // Even after the first hit, keep polling at a slower cadence so the spotlight
      // can MIGRATE to a higher-priority anchor candidate that appears later
      // (e.g. a slide-out panel that opens after the visitor clicks the button).
      poll.current = setInterval(() => {
        const el = findAnchorEl(step);
        if (el) {
          const r = el.getBoundingClientRect();
          setRect((prev) =>
            !prev || prev.top !== r.top || prev.left !== r.left || prev.width !== r.width || prev.height !== r.height
              ? { top: r.top, left: r.left, width: r.width, height: r.height }
              : prev
          );
        } else {
          // Anchor vanished AFTER being found (e.g. the visitor tapped the
          // highlighted "Review with Answers" button directly, so the EndScreen
          // unmounted and the button is gone). Two things must happen:
          //   1. Clear the rect so the green ring stops painting at the now-stale
          //      coordinates of the vanished button.
          //   2. If the step's expected advance was a click on this very anchor
          //      (clickOnAdvance matches a candidate), the visitor has effectively
          //      done the tour's Next for us. Advance the step counter so the
          //      next coach popup lands on the new screen.
          setRect(null);
          const advanceOnCandidates = anchorCandidates(step);
          if (step.clickOnAdvance && advanceOnCandidates.includes(step.clickOnAdvance)) {
            clearInterval(poll.current);
            setI((prev) => (prev === i && i < STEPS.length - 1 ? i + 1 : prev));
          }
        }
      }, 250);
      return () => clearInterval(poll.current);
    }
    poll.current = setInterval(() => {
      tries += 1;
      const el = findAnchorEl(step);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect((prev) =>
          !prev || prev.top !== r.top || prev.left !== r.left || prev.width !== r.width || prev.height !== r.height
            ? { top: r.top, left: r.left, width: r.width, height: r.height }
            : prev
        );
      } else if (tries > 24) {
        clearInterval(poll.current);
        setRect(null);
      }
    }, 150);
    return () => clearInterval(poll.current);
  }, [active, i, step, location.pathname]);

  // Keep the spotlight glued to the target. ResizeObserver + MutationObserver
  // catch layout shifts the instant they happen (font load finishing, image
  // arriving, an accordion expanding, etc.) so the popup and ring never lag
  // behind the real button position. Belt-and-braces with the polling above.
  useEffect(() => {
    if (!active || !anchorCandidates(step).length) return;
    const reposition = () => {
      const el = findAnchorEl(step);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect((prev) =>
          !prev || prev.top !== r.top || prev.left !== r.left || prev.width !== r.width || prev.height !== r.height
            ? { top: r.top, left: r.left, width: r.width, height: r.height }
            : prev
        );
      }
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    // Observe the anchor itself for size changes
    let ro;
    const el = findAnchorEl(step);
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(reposition);
      ro.observe(el);
      // Also observe the document body so we catch layout shifts that move the
      // anchor without resizing it (the EndScreen confetti settling, the
      // Question Review accordion expanding, fonts loading late, etc.).
      try { ro.observe(document.body); } catch { /* noop */ }
    }
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      if (ro) ro.disconnect();
    };
  }, [active, i, step]);

  const finish = useCallback(async () => {
    setActive(false);
    try { await axios.post(`${API}/invites/onboarding-complete`); } catch { /* noop */ }
  }, []);

  const next = () => {
    if (i >= STEPS.length - 1) return finish();
    const cur = STEPS[i];
    const nxt = STEPS[i + 1];
    // If this step opened a side panel / modal, close it before advancing so the
    // next step's spotlight doesn't fight a stale overlay (e.g. Curriculum Map
    // panel still covering the Smartboard button).
    if (cur.closeOnAdvance) {
      const closeEl = document.querySelector(`[data-testid="${cur.closeOnAdvance}"]`);
      if (closeEl) closeEl.click();
    }
    // Gameplay step: dispatch an event the running game listens for so it ends
    // the round cleanly (results screen renders, sounds settle), instead of
    // making the visitor play the whole game out.
    if (cur.endGameOnAdvance) {
      window.dispatchEvent(new CustomEvent('frenchboard:end-game-now'));
    }
    // Some steps need the underlying anchor button activated programmatically
    // when the visitor advances via the tour Next (e.g. Step 9 "Review with
    // Answers" — the visitor's first click of Next puts them face-to-face with
    // the highlighted button; the next click here actually triggers the button
    // so they land *inside* the review experience, never skipping it).
    if (cur.clickOnAdvance) {
      const clickEl = document.querySelector(`[data-testid="${cur.clickOnAdvance}"]`);
      if (clickEl) clickEl.click();
    }
    // Some steps live on a different page than the visitor is currently on
    // (e.g. step 8 spotlights the EndScreen and step 9 lives on /progress —
    // we cannot rely on the visitor to manually navigate). When set, this
    // takes the visitor there as the tour advances.
    if (cur.navigateOnAdvance) {
      navigate(cur.navigateOnAdvance);
    }
    // On nav-kind steps the tour rides the visitor's real tap. If they ask the coach to advance
    // for them, click the actual anchor so the app navigates with its real query params.
    if (cur.kind === 'nav' && anchorCandidates(cur).length) {
      const el = findAnchorEl(cur);
      if (el) { el.click(); return; }
      // Fallback: anchor isn't on the page yet (race during initial navigation, or the user
      // launched the tour while still on /admin and clicked Or-tap-for-me before /browse mounted).
      // Programmatically navigate to the NEXT step's destination and bump the counter so the
      // popup follows the visitor onto the new page instead of silently skipping ahead in place.
      if (nxt && norm(nxt.path) !== norm(cur.path)) {
        navigate(nxt.path);
      }
    }
    setI(i + 1);
  };
  const back = () => {
    if (i <= 0) return;
    const cur = STEPS[i];
    const prev = STEPS[i - 1];
    // Tell the path-watcher we are rewinding, so it does not snap us forward
    // again on the next render where location has updated but i has not yet.
    backIntent.current = true;

    // Run the PREVIOUS step's reverseFromNext action: it knows how to restore the page
    // to that step's expected state (close a modal that's currently open, reopen a panel
    // that got closed on the forward Next, exit ReviewPlayer back to EndScreen, etc.).
    const reverse = prev.reverseFromNext;
    if (reverse && reverse.click) {
      const el = document.querySelector(`[data-testid="${reverse.click}"]`);
      if (el) el.click();
    }

    // If the current step opened a side panel/modal that the previous step doesn't expect
    // open, close it too. Symmetric with next() which closes-on-leaving; here we
    // close-on-arriving so the panel doesn't linger over the previous step's anchor.
    if (cur.closeOnAdvance) {
      const closeEl = document.querySelector(`[data-testid="${cur.closeOnAdvance}"]`);
      if (closeEl) closeEl.click();
    }

    // Navigate to the right URL.
    //
    // reverseFromNext.navigateTo is used when the previous step's state cannot be
    // restored in place (e.g., game state is lost after endGameOnAdvance, or we crossed
    // routes and the in-memory game/review tree was unmounted). In that case we send the
    // visitor to a safe restart point (typically the setup page) AND jump the tour index
    // to the latest step whose path matches that destination, so the popup matches what
    // the visitor sees. This skips a small number of unrecoverable game steps rather than
    // leaving the visitor on a page with no matching popup.
    if (reverse && reverse.navigateTo) {
      let target = i - 1;
      for (let k = i - 1; k >= 0; k--) {
        if (norm(STEPS[k].path) === norm(reverse.navigateTo)) { target = k; break; }
      }
      if (norm(reverse.navigateTo) !== norm(location.pathname)) {
        navigate(reverse.navigateTo);
      }
      setI(target);
      return;
    }

    if (norm(prev.path) !== norm(location.pathname)) {
      navigate(prev.path);
    }
    setI(i - 1);
  };

  if (!active || !user || !step) return null;

  const isDemoLead = user.role === 'invite_lead';
  const hasRect = !!rect;
  const pad = step.ringTight ? 4 : 8;
  const bottomTrim = step.ringBottomTrim || 0;
  let tip;
  if (step.noOverlay && !hasRect) {
    // Floating non-blocking popup with NO anchor. Default pin is TOP-RIGHT (just under
    // the game header) so it never collides with the fixed mute-button at bottom-right;
    // used by the gameplay step where the visitor is mid-play. A step can opt in to
    // BOTTOM-LEFT instead via `floatFallback: 'bottom-left'` (used when the visitor has
    // moved off the anchor's page into a child view — e.g. the Review with Answers screen
    // where the team cards dominate the top and the bottom area is empty).
    if (step.floatFallback === 'bottom-left') {
      tip = { bottom: 24, left: 24, top: 'auto', right: 'auto', zIndex: 100001 };
    } else if (step.floatFallback === 'bottom-right') {
      tip = { bottom: 24, right: 24, top: 'auto', left: 'auto', zIndex: 100001 };
    } else {
      tip = { top: 84, right: 20, bottom: 'auto', left: 'auto', zIndex: 100001 };
    }
  } else if (!hasRect) {
    tip = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  } else if (step.placement === 'left-pin') {
    // Pin popup to the LEFT side of the viewport, sitting ABOVE the anchor's
    // top edge (with a gap) so the popup can never overlap the button itself,
    // no matter how wide the button gets at smaller viewports. We also clamp
    // to the visible viewport so the popup never drifts off-screen on short
    // screens (tested 1280x720, 1440x900, 1920x1080).
    const GAP = 16;
    const cardH = 280;          // generous estimate; real card is ~258px
    const vh = window.innerHeight;
    const minBottom = 24;
    const maxBottom = Math.max(minBottom, vh - cardH - 16); // leave 16px headroom at top
    const desired = hasRect ? vh - rect.top + GAP : minBottom;
    const aboveBottom = Math.min(maxBottom, Math.max(minBottom, desired));
    tip = { left: 24, bottom: aboveBottom, top: 'auto', right: 'auto', transform: 'none', zIndex: 100001 };
  } else {
    const vw = window.innerWidth, vh = window.innerHeight, TW = 340, GAP = 14, p = step.placement || 'bottom';
    let top, left;
    if (p === 'bottom') { top = rect.top + rect.height + GAP; left = rect.left; }
    else if (p === 'top') { top = rect.top - GAP; left = rect.left; }
    else if (p === 'left') { top = rect.top; left = rect.left - TW - GAP; }
    else if (p === 'right') { top = rect.top; left = rect.left + rect.width + GAP; }
    else { top = rect.top + rect.height + GAP; left = rect.left; }
    left = Math.max(12, Math.min(left, vw - TW - 12));
    top = Math.max(12, Math.min(top, vh - 230));
    tip = { top, left, transform: p === 'top' ? 'translateY(-100%)' : 'none' };
  }

  const isLast = i === STEPS.length - 1;
  const isNavStep = step.kind === 'nav';

  return (
    <div data-testid="onboarding-tour" style={{ position: 'fixed', inset: 0, zIndex: 100000, pointerEvents: 'none' }}>
      {/* Spotlight strategy:
          - noOverlay + anchored: a soft coloured ring around the target, NO dim
            (so EndScreen confetti / colours still show through and the page
            keeps feeling alive).
          - noOverlay + no anchor: nothing at all (gameplay step — visitor plays).
          - Normal anchored: classic cutout dim + white outline.
          - Normal no anchor: full dim. */}
      {step.noOverlay ? (hasRect && showCoach ? (
        <div style={{
          position: 'fixed', top: rect.top - pad, left: rect.left - pad,
          width: rect.width + pad * 2, height: rect.height + pad * 2 - bottomTrim,
          borderRadius: 14,
          boxShadow: '0 0 0 3px rgba(255, 255, 255, 0.95), 0 0 0 6px rgba(46, 204, 113, 0.55), 0 0 24px 6px rgba(46, 204, 113, 0.45)',
          transition: 'all 0.25s ease', pointerEvents: 'none',
          animation: 'fb-tour-ring 1.6s ease-in-out infinite',
        }} />
      ) : null) : hasRect ? (
        <div style={{
          position: 'fixed', top: rect.top - pad, left: rect.left - pad,
          width: rect.width + pad * 2, height: rect.height + pad * 2,
          borderRadius: 12, boxShadow: '0 0 0 9999px rgba(15,23,42,0.6)',
          border: '2px solid rgba(255,255,255,0.95)', transition: 'all 0.2s ease', pointerEvents: 'none',
        }} />
      ) : (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', pointerEvents: 'auto' }} />
      )}

      {/* Block accidental taps on the spotlighted element (e.g. the lesson card
          on the "Inside a lesson" step). The visitor must use the tour's Next
          button so they don't fall into a slide view with no exit. */}
      {step.blockAnchorClicks && hasRect ? (
        <div
          data-testid="onboarding-tour-click-blocker"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          style={{
            position: 'fixed', top: rect.top - pad, left: rect.left - pad,
            width: rect.width + pad * 2, height: rect.height + pad * 2,
            borderRadius: 12, background: 'transparent', cursor: 'not-allowed',
            pointerEvents: 'auto', zIndex: 100000,
          }}
        />
      ) : null}

      {showCoach && (
      <div role="dialog" aria-modal="true" data-testid="onboarding-tour-card" style={{
        position: 'fixed', width: 340, maxWidth: '90vw', background: 'white', borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', padding: '1.2rem 1.35rem', pointerEvents: 'auto',
        fontFamily: 'Nunito, sans-serif', animation: 'fb-tour-card-in 0.35s ease-out both', ...tip,
      }}>
        {/* Demo-link visitors cannot skip. Anyone who launched the tour manually (e.g., from
            Settings) gets a Close so they are never trapped. */}
        {!isDemoLead && (
          <button
            data-testid="onboarding-tour-close"
            onClick={() => setActive(false)}
            aria-label="Close tour"
            style={{
              position: 'absolute', top: 10, right: 10, width: 26, height: 26, borderRadius: 13,
              border: 'none', background: '#F1F5F9', color: '#475569', fontSize: '14px',
              lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >×</button>
        )}
        <div style={{ display: 'flex', gap: 5, marginBottom: '0.85rem' }}>
          {STEPS.map((_, k) => (
            <span key={k} style={{
              height: 4, borderRadius: 2, flex: k === i ? 2 : 1,
              background: k === i ? '#1E2140' : (k < i ? '#94A3B8' : '#E2E8F0'), transition: 'all 0.2s',
            }} />
          ))}
        </div>
        <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.05rem', fontWeight: 700, color: '#1A1D20', margin: '0 0 0.4rem' }}>
          {step.title}
        </h3>
        <p style={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.6, margin: '0 0 1.05rem' }}>
          {step.body}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.75rem', color: '#94A3B8' }}>{i + 1} of {STEPS.length}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {i > 0 && <button data-testid="onboarding-tour-back" onClick={back} style={btn(false)}>Back</button>}
            {(() => {
              const need = step.requiresAnswered || 0;
              const gated = need > 0 && answeredCount < need;
              const label = isLast ? 'Go play' : (step.nextLabel || (isNavStep ? 'Or tap for me' : 'Next'));
              return (
                <button
                  data-testid="onboarding-tour-next"
                  onClick={gated ? undefined : next}
                  disabled={gated}
                  title={gated ? 'Tap a couple of options on the board first so the scoreboard fills.' : undefined}
                  style={{ ...btn(true), opacity: gated ? 0.4 : 1, cursor: gated ? 'not-allowed' : 'pointer' }}
                >
                  {label}
                </button>
              );
            })()}
          </div>
        </div>
      </div>
      )}

      <style>{`
        @keyframes fb-tour-ring {
          0%, 100% { box-shadow: 0 0 0 3px rgba(255,255,255,0.95), 0 0 0 6px rgba(46,204,113,0.55), 0 0 24px 6px rgba(46,204,113,0.45); }
          50%      { box-shadow: 0 0 0 3px rgba(255,255,255,0.95), 0 0 0 10px rgba(46,204,113,0.35), 0 0 36px 12px rgba(46,204,113,0.55); }
        }
        @keyframes fb-tour-card-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function btn(primary) {
  return {
    fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: 700,
    padding: '0.5rem 1rem', borderRadius: 10, cursor: 'pointer',
    border: primary ? 'none' : '1px solid #E2E8F0',
    background: primary ? '#1E2140' : 'white', color: primary ? 'white' : '#475569',
  };
}

export default OnboardingTour;
